/**
 * registry — `component_id` → what this build renders for it.
 *
 * The SERVER registry (`backend/widget-native/registry.json`) is the contract;
 * this file is the web runtime's half of it. Every `render: "implemented"`
 * component in that file MUST appear in `KIT` and every `render: "composed"`
 * one MUST expand — `missingImplementations()` diffs the two and the bootstrap
 * browser shows the result, so drift is visible rather than discovered by a
 * blank page.
 *
 * `dataProps` names the props whose STRING values are context paths rather
 * than literals ("totals.calories" vs "Search medicine"). Everything else is
 * literal, with `{…}` interpolation applied to every string. Keeping the list
 * explicit is what stops a copy label like "Tops" from being read as data.
 *
 * `nodeProps` names the props that are component NODES, not values — a grid's
 * `card` template, a page's `sections`, a ring's `center`. They are passed
 * through unresolved: a card binds `item.*`, and there is no item until the
 * grid lays the template out over a row.
 *
 * PORTS ARE THE REGISTRY'S, WITH ONE EXCEPTION. `metaOf().signals`/`.slots`
 * report what a component DECLARES, so they will never list `scrollIntoView`:
 * that one is universal, and the server registry declares it once at the top
 * level (`universal_slots`) rather than 30 times. It is the interpreter's, not
 * the kit's — see the ScrollSink block in `interpreter.jsx` — so a component
 * that appears to be "missing" it is correct as written, and adding it to an
 * entry here would only make one node look more scrollable than its neighbours.
 *
 * THREE resolutions live here, in this order (README §6.2 + §4.5):
 *   1. a native KIT entry            — baked into this build (L0–L2)
 *   2. a `composition` definition    — data, hot-updatable (L3 Composed)
 *   3. a version-gated FALLBACK      — what an app that has NEITHER renders,
 *      so a widget shipping a brand-new component still works on an old app:
 *      function preserved, form degraded. Only a type with no fallback at all
 *      reaches the fail-closed `UnsupportedComponent`.
 *
 * There is no fourth. The taxonomy reorg carried an `aliases` table for one
 * pass — the retired ids a merged entry still answered to — so that merging two
 * components never broke a blueprint mid-flight. Every blueprint now names the
 * canonical id, so the table is gone: a type is either an entry, a composition,
 * or something this build degrades.
 */
import {
  Text, Button, Icon, Image, Badge, Monogram, Video,
  Vertical, Horizontal, Stack, Overlay, Grid, Surface, Padding, CaptureButton, Divider,
} from './components/primitives';
import { SearchField, OptionSelect } from './components/filters';
import { ToolGrid } from './components/toolgrid';
import { StatTileRow, Sheet, EmptyState, ErrorState, PendingCard, NoticeList } from './components/feedback';
import { MediaPicker, Carousel, BeforeAfterCompare } from './components/media';
import { ScanEditor } from './components/scaneditor';
import { DeckCard, DeliveryCard, Disclosure, KeyValueList, NotePanel, ProposalCard, SectionHeader, TextBlock, ThoughtCard, Timeline } from './components/blocks';
import { MessageBubble, ChatThread, ChatComposer, NavList, ChatPage } from './components/chat';
import { CheckList } from './components/checklist';
import { SelectList } from './components/selectlist';
import { MetricRow } from './components/metricrow';
import { ActionRow } from './components/actionrow';
import { TodoCard, DraftCard, DoneRow, SlotPicker, AgentBubble, Greeting, ToggleRow, InboxHeader, InboxRow, InboxText, InboxButton, InboxField, InboxFilter, InboxSteps } from './components/inboxkit';
import { TextComposer, NumberField } from './components/inputs';
import {
  List as A2uiList, Modal as A2uiModal, Tabs as A2uiTabs, AudioPlayer as A2uiAudioPlayer,
  CheckBox as A2uiCheckBox, ChoicePicker as A2uiChoicePicker, DateTimeInput as A2uiDateTimeInput,
  Slider as A2uiSlider, TextField as A2uiTextField,
} from './components/core18';
import { SlotBoard } from './components/board';
import { RoleMediaForm } from './components/RoleMediaForm';
import { ConnectorLayer } from './components/connector';
import { ModeSwitch } from './components/modes';
import { Gauge } from './components/viz';
import { BarChart, CountsCard, DayBars, DayCells, MeterCard, StatHeadline, TallyCard } from './components/charts';
import { Card } from './components/cards';
import { ItemCollection, SessionCollection, ItemCalendar } from './components/collections';
import { CategoryShelf } from './components/shelf';
import { WishlistShelf } from './components/wishlist';
import { StatusPanel } from './components/statuspanel';
import { ThumbnailShelf } from './components/thumbnailshelf';
import { ActivityShelf } from './components/activityshelf';
import { AnswerShelf } from './components/answershelf';
import { PageShell, ItemPage, SessionPage } from './components/chrome';
import HostAdapter from './components/HostAdapter';
import {
  SessionHeroMedia, SessionBeforeAfter, SessionText, SessionMarkdown, SessionAgentSpeech,
  SessionStatGrid, SessionTable, SessionChipList, SessionIconList, SessionNumberedSteps,
  SessionImageGallery, SessionLinkPreview, SessionLocation, SessionShoppingList,
  SessionVideoPlayer, SessionFileCard, SessionWorkerDelegate, SessionActionBar,
} from './components/sessionBlocks';
import { COMPOSITIONS } from './compositions';
import { registerFamilies, resetFamilies } from './families';

/** The app build this kit was baked into — the floor every entry defaults to. */
export const DEFAULT_SINCE = '0.9.0';

export const KIT = {
  // L1 primitives
  Text: { component: Text, dataProps: ['value'] },
  Button: { component: Button, dataProps: ['enabled'] },
  Icon: { component: Icon, dataProps: [] },
  Image: { component: Image, dataProps: ['src'] },
  Badge: { component: Badge, dataProps: ['tone', 'text'] },
  // Motion, and the plate a row with no picture earns. Both degrade to
  // something older that still carries the meaning (a poster · initials).
  // An app with no Image either exhausts the chain, which OMITS the node —
  // the honest end for a component whose whole content is the media.
  Video: { component: Video, dataProps: ['src', 'poster'], fallback: { type: 'Image', props: { src: { $: 'props.poster' } } } },
  Monogram: { component: Monogram, dataProps: ['value', 'text', 'seed'], fallback: 'Text' },
  // ONE value-out-of-max gauge in three SHAPES (bar · segments · ring). The
  // straight one and the round one were the same contract drawn two ways — the
  // old registry said so itself, by declaring the round one's fallback to BE
  // the straight one — so the shape is a `variant`, not an identity.
  Gauge: { component: Gauge, dataProps: ['value', 'max', 'target'], nodeProps: ['center'] },
  // Linear layout (Qt VBox/HBox). `Stack` is now the z-overlap strip ONLY.
  Vertical: { component: Vertical, dataProps: [] },
  Horizontal: { component: Horizontal, dataProps: [] },
  Stack: { component: Stack, dataProps: [] },
  // Layering is the ONLY thing Vertical/Horizontal cannot do, so an app without
  // Overlay renders the same children stacked in flow (a column) — legible, just
  // not layered.
  Overlay: { component: Overlay, dataProps: [], fallback: 'Vertical' },
  Grid: { component: Grid, dataProps: [] },
  Surface: { component: Surface, dataProps: [] },
  Padding: { component: Padding, dataProps: [], fallback: 'Vertical', since: '0.9.6' },
  Divider: { component: Divider, dataProps: [] },
  CaptureButton: { component: CaptureButton, dataProps: [], spec: true },

  // L2 common
  SearchField: { component: SearchField, dataProps: [] },
  // ONE option picker for the three that were one: a chip filter bar, a
  // choice-chip field and an option catalogue held the same selection over the
  // same option shape and differed in two DEFAULTS (`allOption`, `clearable`)
  // and one drawing (`layout`). `options` may be a BIND (the chip set a feed
  // earned), so it is declared as a data prop — a literal array is unaffected.
  OptionSelect: { component: OptionSelect, dataProps: ['options'] },
  // The LAUNCHER half of the same family: a deck of tiles that FIRE rather
  // than a row of chips that hold. `tiles` is authored, never bound — a
  // toolbox is a set of doors the widget declares, not rows a feed earned —
  // so it names no data props, and it is not a CONTAINER: it lays its own
  // tiles out from `tiles`, never from `children`.
  ToolGrid: { component: ToolGrid, dataProps: [] },
  // ── a2ui core-18 (basic catalog verbatim; introduced_in_app 0.10.0) ──────
  // `List` is the a2ui core directional container (NOT our ItemCollection).
  List: { component: A2uiList, dataProps: [], since: '0.10.0' },
  Modal: { component: A2uiModal, dataProps: [], since: '0.10.0' },
  Tabs: { component: A2uiTabs, dataProps: [], since: '0.10.0' },
  AudioPlayer: { component: A2uiAudioPlayer, dataProps: ['url'], since: '0.10.0' },
  CheckBox: {
    component: A2uiCheckBox, dataProps: ['value'], since: '0.10.0',
    fallback: { type: 'Text', props: { value: { $: 'props.label' } } },
  },
  Slider: { component: A2uiSlider, dataProps: ['value'], since: '0.10.0' },
  TextField: {
    component: A2uiTextField, dataProps: ['value'], since: '0.10.0',
    fallback: { type: 'TextComposer', props: { placeholder: { $: 'props.placeholder' } } },
  },
  ChoicePicker: { component: A2uiChoicePicker, dataProps: ['value'], since: '0.10.0' },
  DateTimeInput: {
    component: A2uiDateTimeInput, dataProps: ['value'], since: '0.10.0',
    fallback: { type: 'Text', props: { value: { $: 'props.value' } } },
  },
  StatTileRow: { component: StatTileRow, dataProps: [] },
  Sheet: { component: Sheet, dataProps: [] },
  EmptyState: { component: EmptyState, dataProps: [] },
  // A failed FETCH is not an empty widget — but an old app that only knows
  // "nothing here" still says something true.
  ErrorState: { component: ErrorState, dataProps: [], fallback: 'EmptyState' },
  PendingCard: { component: PendingCard, dataProps: [] },
  // A card resolves its OWN fields against the item scope (title, badges[].tone,
  // meta[].value — at every depth), so nothing is pre-resolved for it here.
  // ONE card, for every collection: a card owns no identity, so there is no
  // lifecycle for a second card entry to differ over.
  Card: { component: Card, dataProps: [] },
  // ONE bound tone-row list in two readings: shape-coded advisories, and the
  // swatch·count·word tally that used to be a second entry one optional field
  // away. A build without it renders the rows stacked, which is what the
  // absorbed entry already declared.
  NoticeList: { component: NoticeList, dataProps: ['items'], fallback: 'Vertical' },
  MediaPicker: { component: MediaPicker, dataProps: ['items'] },
  // The POST-CAPTURE surface: the pages the host just took, rotated and
  // filtered client-side, emitted on confirm. `images` is a data prop because
  // the page list is what the host handed the page (`page_args.images`), not
  // copy the blueprint wrote — and it is NOT a CONTAINER: it lays its own pages
  // out from `images`, never from `children`.
  ScanEditor: { component: ScanEditor, dataProps: ['images'] },
  BarChart: { component: BarChart, dataProps: ['bars', 'target', 'selected'] },
  // One short passage with its name on it. Degrades to the passage itself.
  NotePanel: {
    component: NotePanel, dataProps: ['text', 'lines'],
    fallback: { type: 'TextBlock', props: { value: [{ $: 'props.lead' }, { $: 'props.text' }, '{props.lines}'] } },
  },
  // A count to act on. Degrades to the number itself.
  TallyCard: {
    component: TallyCard, dataProps: ['value', 'action'],
    fallback: { type: 'Text', props: { value: { $: 'props.value' } } },
  },
  // A reading against its cap, with its parts. Degrades to the same value out of the same max, as a bar.
  MeterCard: {
    component: MeterCard, dataProps: ['value', 'max', 'parts'],
    fallback: { type: 'Gauge', props: { variant: 'bar', value: { $: 'props.value' }, max: { $: 'props.max' } } },
  },
  // The front of a deck. Degrades to a text Card carrying the same three lines.
  DeckCard: {
    component: DeckCard, dataProps: ['title', 'sub', 'covers'],
    fallback: {
      type: 'Card',
      props: { layout: 'text', eyebrow: { $: 'props.eyebrow' }, title: { $: 'props.title' }, subtitle: { $: 'props.sub' } },
    },
  },
  // The agent stepping forward to say one thing. Degrades to the remark itself.
  ThoughtCard: {
    component: ThoughtCard, dataProps: ['text'],
    fallback: { type: 'TextBlock', props: { value: { $: 'props.text' } } },
  },
  // Several counts side by side. Degrades to the same counts as an inert tile row.
  CountsCard: {
    component: CountsCard, dataProps: ['counts'],
    fallback: { type: 'StatTileRow', props: { variant: 'metric', tappable: false, tiles: { $: 'props.counts' } } },
  },
  // A finished run reported back. Degrades to the plain media-left Card.
  DeliveryCard: {
    component: DeliveryCard, dataProps: ['media', 'facts', 'quote', 'action'],
    fallback: {
      type: 'Card',
      props: {
        layout: 'media-left', media: { $: 'props.media' },
        title: { $: 'props.title' }, subtitle: { $: 'props.subtitle' },
      },
    },
  },
  // A week at a glance. Degrades to the series chart over the same days.
  DayBars: {
    component: DayBars, dataProps: ['days'],
    fallback: { type: 'BarChart', props: { bars: { $: 'props.days' } } },
  },
  // ONE number said loudly. Degrades to the number itself: an app without
  // the headline still tells the reader what it is.
  StatHeadline: {
    component: StatHeadline, dataProps: ['value', 'glyphs'],
    fallback: { type: 'Text', props: { value: { $: 'props.value' } } },
  },
  // A month of cells the blueprint already decided. Degrades to the L2
  // calendar over the same day rows: the grid without the glyphs.
  DayCells: {
    component: DayCells, dataProps: ['days'],
    fallback: { type: 'ItemCalendar', props: { items: { $: 'props.days' }, dateField: 'date' } },
  },

  // …the small structural blocks every detail surface repeats. Each degrades
  // to the primitive it is a shaped version of: a heading is a Text, a list of
  // rows is a Stack of them.
  SectionHeader: {
    component: SectionHeader, dataProps: ['title', 'count', 'sub', 'meta'],
    fallback: { type: 'Text', props: { value: { $: 'props.title' } } },
  },
  KeyValueList: { component: KeyValueList, dataProps: ['items'], fallback: 'Vertical' },
  TextBlock: { component: TextBlock, dataProps: ['value'], fallback: 'Text' },
  Disclosure: { component: Disclosure, dataProps: ['summary'], fallback: 'Vertical' },
  Timeline: { component: Timeline, dataProps: ['nodes'], fallback: 'Vertical' },
  // A proposal the user answers. Degrades to the plain media-left Card it
  // is a fuller reading of: the picture, the title, the subtitle.
  ProposalCard: {
    component: ProposalCard, dataProps: ['media', 'chips', 'note', 'actions'],
    fallback: {
      type: 'Card',
      props: {
        layout: 'media-left', media: { $: 'props.media' },
        title: { $: 'props.title' }, subtitle: { $: 'props.subtitle' },
      },
    },
  },

  // One turn of a conversation. It degrades to a TextBlock: the words survive,
  // the side does not.
  MessageBubble: {
    component: MessageBubble,
    // `role`/`status` are enums whose literals collide with context roots
    // (`user`), so they are never read as paths — bind them with `{$: …}`.
    dataProps: ['value'],
    fallback: 'TextBlock',
  },
  // A conversation: one MessageBubble per bound row. It degrades to a
  // NoticeList, which keeps every turn's words and loses the sides.
  ChatThread: {
    component: ChatThread,
    dataProps: ['messages', 'suggestions'],
    fallback: { type: 'NoticeList', props: { items: { $: 'props.messages' } } },
  },
  ChatComposer: { component: ChatComposer, dataProps: ['seed'], fallback: 'TextComposer' },
  NavList: {
    component: NavList,
    dataProps: ['items'],
    fallback: { type: 'OptionSelect', props: { options: { $: 'props.items' } } },
  },
  ChatPage: { component: ChatPage, dataProps: ['title'], fallback: 'PageShell' },
  // A list of things to do whose DONE state the data owns; the row only asks (`toggled`).
  CheckList: {
    component: CheckList,
    dataProps: ['items'],
    fallback: { type: 'NoticeList', props: { items: { $: 'props.items' } } },
  },
  ActionRow: {
    component: ActionRow,
    dataProps: [],
    since: '0.10.2',
    fallback: { type: 'Button', props: { label: { $: 'props.title' } } },
  },
  MetricRow: {
    component: MetricRow,
    dataProps: ['items'],
    since: '0.10.2',
    fallback: { type: 'KeyValueList', props: { items: { $: 'props.items' } } },
  },
  SelectList: {
    component: SelectList,
    dataProps: ['items'],
    since: '0.10.2',
    fallback: { type: 'NoticeList', props: { items: { $: 'props.items' } } },
  },
  TodoCard: {
    component: TodoCard,
    dataProps: ['choices'],
    since: '1.1.2',
    fallback: { type: 'Card', props: { layout: 'text', eyebrow: { $: 'props.name' }, title: { $: 'props.title' }, subtitle: { $: 'props.note' } } },
  },
  DraftCard: {
    component: DraftCard,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Card', props: { layout: 'text', eyebrow: { $: 'props.eyebrow' }, title: { $: 'props.title' }, subtitle: { $: 'props.preview' } } },
  },
  DoneRow: {
    component: DoneRow,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Card', props: { layout: 'text', title: { $: 'props.title' }, subtitle: { $: 'props.sub' } } },
  },
  SlotPicker: {
    component: SlotPicker,
    dataProps: ['slots'],
    since: '1.1.2',
    fallback: { type: 'SelectList', props: { items: { $: 'props.slots' }, defaultSelected: 'none' } },
  },
  AgentBubble: { component: AgentBubble, dataProps: [], since: '1.1.2', fallback: 'Vertical' },
  Greeting: {
    component: Greeting,
    dataProps: ['faces'],
    since: '1.1.2',
    fallback: { type: 'Text', props: { value: { $: 'props.title' } } },
  },
  ToggleRow: {
    component: ToggleRow,
    dataProps: ['value'],
    since: '1.1.2',
    fallback: { type: 'CheckBox', props: { label: { $: 'props.title' }, value: { $: 'props.value' } } },
  },
  InboxHeader: {
    component: InboxHeader,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Text', props: { value: { $: 'props.title' } } },
  },
  InboxRow: {
    component: InboxRow,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Card', props: { layout: 'text', title: { $: 'props.title' }, subtitle: { $: 'props.sub' } } },
  },
  InboxText: {
    component: InboxText,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Text', props: { value: { $: 'props.value' } } },
  },
  InboxButton: {
    component: InboxButton,
    dataProps: [],
    since: '1.1.2',
    fallback: { type: 'Button', props: { label: { $: 'props.label' }, block: { $: 'props.block' }, enabled: { $: 'props.enabled' } } },
  },
  InboxField: {
    component: InboxField,
    dataProps: ['value'],
    since: '1.1.2',
    fallback: { type: 'TextField', props: { value: { $: 'props.value' }, placeholder: { $: 'props.placeholder' } } },
  },
  InboxFilter: {
    component: InboxFilter,
    dataProps: ['options'],
    since: '1.1.2',
    fallback: { type: 'OptionSelect', props: { options: { $: 'props.options' }, allOption: true, allLabel: { $: 'props.allLabel' }, mode: 'single', layout: 'chips' } },
  },
  InboxSteps: {
    component: InboxSteps,
    dataProps: ['steps'],
    since: '1.1.2',
    fallback: { type: 'Text', props: { value: { $: 'props.doneLabel' } } },
  },

  // ONE exclusive mode switch in two shapes. A pill tab bar and a segmented
  // control routed children by `region` identically; only the drawing differed,
  // so the drawing is the variant.
  ModeSwitch: { component: ModeSwitch, dataProps: ['options', 'select'], fallback: 'OptionSelect' },

  // The ROLE FORM: N ordered photo wells, each filled by one host `pickMedia`,
  // whose completeness gates a submit. `MediaPicker` is its degradation and not
  // its equal — an app without this one still lets the user pick the photos,
  // just as an unordered bag with no idea which is which.
  RoleMediaForm: {
    component: RoleMediaForm,
    dataProps: ['roles', 'compositionReference'],
    fallback: { type: 'MediaPicker', props: { add: true } },
  },

  // The PLACED plate: N `%` boxes on one fixed-aspect surface, each filled from
  // the bound list by a matching field. `Grid` divides, `Overlay` layers, and
  // neither PLACES — so the arrangement had no expression at all.
  SlotBoard: { component: SlotBoard, dataProps: ['slots'], nodeProps: ['cell'] },
  // The MEASURED edge: one root box fanned into N bound rows. It owns both ends
  // (`from` is a node, `to` a per-row template) because an edge drawn between
  // boxes somebody else laid out can disagree with where they ended up.
  ConnectorLayer: { component: ConnectorLayer, dataProps: [], nodeProps: ['from', 'to'] },

  // …and the free-text half of a composed request.
  TextComposer: {
    component: TextComposer, dataProps: ['seed'],
    // the composer's FUNCTION is "say something and send it" — an app without
    // one still gets there through the host's own capture surface
    fallback: { type: 'Button', props: { label: { $: 'props.submitLabel' } } },
  },

  // …the two detail-media surfaces. The stage absorbed the paged slide track
  // it used to declare as its own fallback — a near-copy with two props turned
  // off. Its degradation is now the honest one: ONE picture, the current
  // subject, which is what a stage is for.
  Carousel: {
    component: Carousel, dataProps: ['slides'],
    fallback: { type: 'Image', props: { src: { $: 'props.slides' } } },
  },
  BeforeAfterCompare: {
    component: BeforeAfterCompare, dataProps: ['before', 'after'],
    // one picture instead of two: the AFTER is the one the page is about
    fallback: { type: 'Image', props: { src: { $: 'props.after' } } },
  },
  // …the bounded number, with its steppers and its "nothing to save yet" seal.
  NumberField: { component: NumberField, dataProps: ['value'] },
  // …and the L2 capability modules: generic too, they just own the data
  // lifecycle (identity, CRUD, pending, live-update) instead of pixels.
  PageShell: { component: PageShell, dataProps: [] },
  // `items` is the LITERAL source a rail was authored with; a bound collection
  // uses `bind`, and declaring both costs nothing. A rail is NOT a different
  // kind of collection — it is this one, laid out sideways (`layout: "rail"`).
  ItemCollection: { component: ItemCollection, dataProps: ['items'], nodeProps: ['card'] },
  // The pill row + the grid as one: the filter is wired INSIDE, so a caller
  // connects nothing. Without the shelf an old app still gets the items.
  CategoryShelf: {
    component: CategoryShelf, dataProps: ['options', 'items', 'select'], nodeProps: ['card'],
    fallback: 'ItemCollection',
  },
  // The header + the bound list as one: the caller's own `card` template
  // carries the CTA (a Button whose `activated` bubbles out as itemActivated).
  WishlistShelf: {
    component: WishlistShelf, dataProps: ['items'], nodeProps: ['card'],
    fallback: 'ItemCollection',
  },
  // The metric row shares the panel's OWN bind, so the fallback tile row
  // reads the same values without a prop remap.
  StatusPanel: {
    component: StatusPanel, dataProps: ['status', 'calloutLead', 'calloutBody', 'featured'], nodeProps: ['card'],
    fallback: 'StatTileRow',
  },
  // A header over a horizontally scrolling rail of the caller's own `card`
  // cells.
  ThumbnailShelf: {
    component: ThumbnailShelf, dataProps: ['items'], nodeProps: ['card'],
    fallback: 'ItemCollection',
  },
  // The header + the bound list as one, closed by a relative-time read.
  ActivityShelf: {
    component: ActivityShelf, dataProps: ['items'], nodeProps: ['card'],
    fallback: 'ItemCollection',
  },
  // The pill row + the rail as one, where the pick also retells the prose:
  // the active option's own `caption` renders under the pills.
  AnswerShelf: {
    component: AnswerShelf, dataProps: ['options', 'items', 'select', 'caption'], nodeProps: ['card'],
    fallback: 'ItemCollection',
  },
  SessionCollection: { component: SessionCollection, dataProps: ['items'], nodeProps: ['card'] },
  // A month is one way to read a dated list; a list is the other. Same rows,
  // same card, so the substitution is straight.
  ItemCalendar: {
    component: ItemCalendar, dataProps: ['items'], nodeProps: ['card'], fallback: 'ItemCollection',
  },
  ItemPage: { component: ItemPage, dataProps: ['focus'], nodeProps: ['sections'] },
  SessionPage: { component: SessionPage, dataProps: ['focus'], nodeProps: ['sections'] },
  host: { component: HostAdapter, dataProps: [] },

  // ── THE SESSION BLOCKS (v0.9 `native-ir`) ─────────────────────────────
  //
  // Sixteen L2 modules that are NOT part of a blueprint: the model authors
  // one of them as DATA between `<vi-block>…</vi-block>`, the WAL reducer
  // projects it into one `session_block` card, and the session surface runs
  // that card's `data.node` through this same interpreter as a NODE.
  //
  // EVERY PROP IS A `nodeProp`, WHICH IS THE WHOLE POINT. A blueprint node's
  // props are expressions — paths to resolve, `{…}` to interpolate — because a
  // widget page has a context to read. A SESSION BLOCK HAS NONE: it is an
  // immutable snapshot of one answer, its fields are literal values the model
  // wrote, and there is nothing for a path to point at. Declaring them
  // verbatim is what guarantees that a paragraph of prose containing braces
  // reaches the reader as the words the model wrote rather than as an
  // unresolvable read that blanks itself.
  //
  // `session: true` marks the SURFACE, not the behaviour: these are drawn
  // under the session's `--vi-*` tokens (no accent, no display face — see
  // `buildSessionTokenStyle`), which is also how the bootstrap browser knows
  // to preview them on the surface they actually ship on.
  hero_media: {
    component: SessionHeroMedia, session: true, dataProps: [],
    // `slot` / `error` are the fire-and-continue pair every media block carries
    // while its asset is still being generated (P2-A) — verbatim like the rest,
    // so a server sentence with braces in it survives as words.
    //
    // `beforeImageUrl` is NOT declared any more: a comparison is `before_after`,
    // and a hero is ONE picture. The component still RENDERS a stored one (the
    // legacy read for sessions already in the database) — the schema simply
    // stops advertising it, so nothing new is authored that way.
    // `progress*` is the NON-TERMINAL marker a `media_progress` tick leaves on a
    // pending node (CONTRACT §5) — verbatim for the same reason `error` is: a
    // provider's label is prose, and a terminal fill clears all three.
    nodeProps: [
      'imageUrl', 'title', 'subtitle', 'slot', 'error',
      'progressStage', 'progressPct', 'progressLabel',
    ],
  },
  before_after: {
    component: SessionBeforeAfter, session: true, dataProps: [],
    // BOTH halves are fillable, but only the AFTER one waits: `slot`/`error`
    // belong to `afterImageUrl` (`session_wal_reducer._NATIVE_SLOT_REF_FIELD`
    // names that field for this type), while `beforeImageUrl` is a ref the user
    // already handed over.
    nodeProps: [
      'beforeImageUrl', 'afterImageUrl', 'title', 'subtitle', 'slot', 'error',
      'progressStage', 'progressPct', 'progressLabel',
    ],
  },
  text: {
    component: SessionText, session: true, dataProps: [], nodeProps: ['title', 'content'],
  },
  markdown: {
    component: SessionMarkdown, session: true, dataProps: [],
    nodeProps: ['title', 'paragraphs', 'footer'],
  },
  agent_speech: {
    component: SessionAgentSpeech, session: true, dataProps: [], nodeProps: ['text'],
  },
  stat_grid: {
    component: SessionStatGrid, session: true, dataProps: [], nodeProps: ['items'],
  },
  table: {
    component: SessionTable, session: true, dataProps: [],
    // `thumbnails` (B18 P-A) is the OPTIONAL leading picture column, one entry
    // per row. External CDN addresses authored inline — never `media://` slots.
    nodeProps: ['title', 'headers', 'icons', 'rows', 'footer', 'thumbnails'],
  },
  chip_list: {
    component: SessionChipList, session: true, dataProps: [],
    nodeProps: ['title', 'chips', 'selected', 'footer'],
  },
  icon_list: {
    component: SessionIconList, session: true, dataProps: [], nodeProps: ['title', 'items'],
  },
  numbered_steps: {
    component: SessionNumberedSteps, session: true, dataProps: [],
    nodeProps: ['title', 'steps', 'footer'],
  },
  image_gallery: {
    component: SessionImageGallery, session: true, dataProps: [],
    nodeProps: ['title', 'images', 'footer', 'slot', 'error'],
  },
  link_preview: {
    component: SessionLinkPreview, session: true, dataProps: [], nodeProps: ['links'],
  },
  location: {
    component: SessionLocation, session: true, dataProps: [],
    nodeProps: ['lat', 'lng', 'label', 'address', 'mapUrl'],
  },
  shopping_list: {
    component: SessionShoppingList, session: true, dataProps: [],
    nodeProps: ['title', 'items', 'totalEstimate', 'storeSuggestion'],
  },
  video_player: {
    component: SessionVideoPlayer, session: true, dataProps: [],
    nodeProps: [
      'title', 'url', 'poster', 'description', 'duration', 'slot', 'error',
      'progressStage', 'progressPct', 'progressLabel',
    ],
  },
  file_card: {
    component: SessionFileCard, session: true, dataProps: [],
    // `url` is already RESOLVED when this block is minted (the reducer's rescue
    // mints it AT DELIVERY, keyed by the ref), so unlike the generated-media
    // blocks it normally arrives ready. `slot`/`error` are declared all the same
    // — an authored one may claim a slot, and a worker whose upload failed
    // writes the sentence the block shows instead of a link to nothing.
    nodeProps: [
      'title', 'url', 'fileType', 'fileName', 'size', 'pageCount', 'description',
      'slot', 'error', 'progressStage', 'progressPct', 'progressLabel',
    ],
  },
  worker_delegate: {
    component: SessionWorkerDelegate, session: true, dataProps: [],
    // The delivery contract a `dispatch_worker` mints on fire (B18 P-B). It
    // carries BOTH clocks: `status` (the worker's) and `progress*` (the
    // `media_progress` ticks that land on its `wd-<execId>` slot), plus the
    // `deliverables[]` the fills append into. Verbatim like every other block —
    // a caption the model wrote must survive braces and all.
    nodeProps: [
      'title', 'task', 'status', 'slot', 'deliverables',
      'progressStage', 'progressPct', 'progressLabel', 'error',
    ],
  },
  action_bar: {
    component: SessionActionBar, session: true, dataProps: [], nodeProps: ['actions'],
  },

  // L3 Composed is NOT here: a composed module is a `composition` (see
  // compositions.js), which is exactly what makes it hot-updatable.
};

/** Components that render children (the ones a blueprint composes into). */
export const CONTAINERS = new Set([
  'PageShell', 'Vertical', 'Horizontal', 'Stack', 'Overlay', 'Grid', 'Surface', 'Sheet',
  'ModeSwitch', 'Disclosure', 'Carousel', 'AgentBubble',
]);
// SlotBoard and ConnectorLayer are deliberately NOT here: they place their
// content from `slots`/`from`/`to` rather than laying out declared `children`,
// so a blueprint that nests children under them would be writing something
// neither one renders.

// ── the installable half: registry.json + hot-shipped compositions ─────────

/** `registry.json` components by id, once `registerRegistry` has run. */
let REGISTERED = Object.create(null);
/** Compositions installed at runtime (a bundle, a test, a fetched registry). */
let INSTALLED = Object.create(null);

const metaCache = new Map();
const kitCache = new Map();

function clearCaches() {
  metaCache.clear();
  kitCache.clear();
}

/**
 * Install the SERVER registry: its `composition` bodies, `dataProps`/
 * `nodeProps`, `fallback` and `introduced_in_app` override this file's
 * bundled defaults. This is the hot-update seam — the studio calls it after
 * fetching `/api/widget-native/registry`, so a composition edited on the
 * server renders without a new web bundle.
 */
export function registerRegistry(registryJson) {
  const next = Object.create(null);
  for (const c of registryJson?.components || []) if (c?.id) next[c.id] = c;
  REGISTERED = next;
  // …including each component's FAMILY, which the layout reads (see
  // `families.js`): a component hot-shipped into a family this build has never
  // seen must be laid out by its own declaration, not by the bundled snapshot.
  registerFamilies(registryJson);
  clearCaches();
}

/**
 * Install ONE component DEFINITION at runtime — a widget bundle shipping its
 * own L3, or a test. A def may carry a `composition` (what to expand), a
 * `fallback` + `introduced_in_app` (how to degrade on a build that lacks it),
 * and its `dataProps`/`nodeProps`. Declaring only the second half is the
 * normal case for a component this build cannot render at all.
 */
export function registerComponentDef(id, def) {
  INSTALLED[id] = def;
  clearCaches();
}

/** Drop a runtime-installed definition (or all of them). */
export function unregisterComponentDef(id = null) {
  if (id == null) INSTALLED = Object.create(null);
  else delete INSTALLED[id];
  clearCaches();
}

/** Forget everything installed at runtime — the bundled defaults remain. */
export function resetRegistry() {
  REGISTERED = Object.create(null);
  INSTALLED = Object.create(null);
  resetFamilies();
  clearCaches();
}

const has = (o, k) => Boolean(o) && Object.prototype.hasOwnProperty.call(o, k);

/** First source that DECLARES `key` (a declared `null` is an answer). */
function declared(sources, key, dflt) {
  for (const s of sources) if (has(s, key)) return s[key];
  return dflt;
}

/**
 * Everything the runtime knows about a type, whether or not it can render it:
 * `{ since, fallback, hasFallback, dataProps, nodeProps, composition }`.
 *
 * Sources in precedence order: a runtime-installed composition, the server
 * registry entry, the bundled composition, the native KIT entry. A hot-shipped
 * component this build cannot render still has meta (its `dataProps` and its
 * `fallback`), which is precisely what lets it degrade rather than vanish.
 */
export function metaOf(type) {
  if (metaCache.has(type)) return metaCache.get(type);
  const sources = [INSTALLED[type], REGISTERED[type], COMPOSITIONS[type], KIT[type]].filter(Boolean);
  const kit = KIT[type];
  const meta = {
    since: declared(sources, 'introduced_in_app', declared(sources, 'since', DEFAULT_SINCE)),
    fallback: declared(sources, 'fallback', undefined),
    hasFallback: sources.some((s) => has(s, 'fallback')),
    dataProps: declared(sources, 'dataProps', kit?.dataProps || []),
    nodeProps: declared(sources, 'nodeProps', kit?.nodeProps || []),
    composition: declared(sources, 'composition', undefined),
    signals: declared(sources, 'signals', []),
    slots: declared(sources, 'slots', []),
  };
  metaCache.set(type, meta);
  return meta;
}

/** The composed definition for a type, or null. */
export function lookupComposition(type) {
  const meta = metaOf(type);
  return meta.composition ? meta : null;
}

/** "0.9.10" vs "0.9.2" numerically, segment by segment. */
function cmpVersion(a, b) {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/**
 * The VERSION GATE (§4.5): a component renders natively only if the target app
 * build is at least the build that introduced it. `targetBuild` absent = latest
 * = every component available, which is what the app and every existing caller
 * get; the studio can pass an older build to SEE what an old app would render.
 */
export function meetsBuild(type, targetBuild) {
  if (!targetBuild) return true;
  return cmpVersion(metaOf(type).since, targetBuild) <= 0;
}

/**
 * Kit entry for a `component_id`, or null when this build has neither a native
 * implementation nor a composition for it.
 *
 * A composed type answers with a SYNTHETIC entry — `{ composition, dataProps,
 * nodeProps }` and no `component` — which the interpreter renders by expanding
 * the body inside a `props` scope (see `interpreter.jsx`). Memoised so the
 * entry (and therefore the React element type) is stable across renders.
 */
export function lookupKit(type) {
  if (kitCache.has(type)) return kitCache.get(type);
  const meta = metaOf(type);
  const kit = KIT[type];
  let entry = null;
  if (kit) {
    // Registry-declared prop specs win over the hardcoded defaults, so a
    // component defined only in the registry can still declare its paths.
    entry = (meta.dataProps === kit.dataProps && meta.nodeProps === kit.nodeProps)
      ? kit
      : { ...kit, dataProps: meta.dataProps, nodeProps: meta.nodeProps };
  } else if (meta.composition) {
    entry = {
      component: null,
      composition: meta.composition,
      composed: meta,
      dataProps: meta.dataProps,
      nodeProps: meta.nodeProps,
    };
  }
  kitCache.set(type, entry);
  return entry;
}

/** The `{dataProps,nodeProps}` a node's OWN type declares (even if unrenderable). */
export function propSpecFor(type) {
  const meta = metaOf(type);
  return { dataProps: meta.dataProps, nodeProps: meta.nodeProps };
}

/** `"Stack"` | `{type,props}` | `["A","B"]` | null → an ordered candidate list. */
function candidates(fallback) {
  if (fallback == null) return [];
  const list = Array.isArray(fallback) ? fallback : [fallback];
  return list
    .map((f) => (typeof f === 'string' ? { type: f, props: null } : (f?.type ? { type: f.type, props: f.props || null } : null)))
    .filter(Boolean);
}

/**
 * GRACEFUL DEGRADATION (§4.5) — resolve what THIS build renders for a type.
 *
 * Walks the declared `fallback` chain (breadth-first, in declared order, each
 * hop may itself fall back) until it finds a component this build can render
 * at the target app build. Returns one of:
 *   { status:'kit'|'composition', type, kit, remap, degradedFrom }
 *   { status:'omit', … }        a declared `null` fallback, or an exhausted
 *                               chain — the component is not essential, so the
 *                               page renders WITHOUT it rather than half-render
 *   { status:'unknown', … }     no impl and no fallback declared anywhere: an
 *                               authoring error, and the only case that still
 *                               reaches `UnsupportedComponent`
 *
 * `remap` is the last declared props remap on the path — the fallback's own
 * props expression, evaluated by the interpreter against `props.*` (the
 * original node's resolved props) and `data.*` (its bind), exactly like a
 * composition body.
 */
export function resolveComponent(type, targetBuild = null) {
  if (!type) return { status: 'unknown', type, reason: 'no type' };
  const seen = new Set();
  const queue = [{ type, remap: null, degraded: false }];
  let declaredAny = false;

  while (queue.length) {
    const { type: cur, remap, degraded } = queue.shift();
    if (!cur || seen.has(cur)) continue;
    seen.add(cur);

    const kit = lookupKit(cur);
    if (kit && meetsBuild(cur, targetBuild)) {
      return {
        status: kit.composition ? 'composition' : 'kit',
        type: cur,
        kit,
        remap: remap || null,
        degradedFrom: degraded ? type : null,
      };
    }

    const meta = metaOf(cur);
    if (!meta.hasFallback) continue;
    declaredAny = true;
    if (meta.fallback === null) {
      return { status: 'omit', type, reason: 'decorative', degradedFrom: cur };
    }
    for (const c of candidates(meta.fallback)) {
      queue.push({ type: c.type, remap: c.props || remap, degraded: true });
    }
  }

  return declaredAny
    ? { status: 'omit', type, reason: 'fallback chain exhausted' }
    : { status: 'unknown', type, reason: targetBuild ? `needs app ${metaOf(type).since}` : 'no implementation' };
}

/**
 * Registry components this runtime does NOT honour: `render:"implemented"`
 * with no kit entry, or `render:"composed"` with nothing to expand. Should
 * always be empty — the bootstrap browser surfaces it so a new registry entry
 * can't ship as a silently blank card. `render:"spec"` is excused by design.
 */
export function missingImplementations(registryJson) {
  const list = registryJson?.components || [];
  return list
    .filter((c) => (c.render === 'implemented' && !KIT[c.id])
      || (c.render === 'composed' && !(c.composition || lookupComposition(c.id))))
    .map((c) => c.id);
}

/**
 * The RETIRED native-ir session blocks. The session surface is a2ui core-only now (the
 * registry lists NO session customs — DEC-3 / user 2026-09-18), so these have no registry
 * contract any more; the kit keeps their builders only to replay sessions stored before
 * the retirement (the runner's GATE projects a stray name to core on every new turn).
 */
export const RETIRED_SESSION_BLOCKS = new Set([
  'hero_media', 'before_after', 'text', 'markdown', 'agent_speech', 'stat_grid', 'table',
  'chip_list', 'icon_list', 'numbered_steps', 'image_gallery', 'link_preview', 'location',
  'shopping_list', 'video_player', 'action_bar', 'file_card', 'worker_delegate',
]);

/** Kit ids with no registry entry — the opposite drift (retired session blocks excepted). */
export function extraImplementations(registryJson) {
  const known = new Set((registryJson?.components || []).map((c) => c.id));
  return Object.keys(KIT).filter((id) => !known.has(id) && !RETIRED_SESSION_BLOCKS.has(id));
}
